// frontend/src/shared/utils/formSchema.ts
// AM-006: definition_json از fields تخت به header/body/footer تغییر کرد.

export type FormFieldType =
  | "text" | "number" | "textarea" | "select" | "checkbox" | "date" | "repeater";

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface ChainRule {
  operator: "eq" | "lt" | "lte" | "gt" | "gte" | "between" | "in";
  value: number | string | [number, number] | string[];
  target_family_code: string;
}

export interface FormFieldDef {
  key: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  help?: string;
  source?: string;
  options?: FormFieldOption[];
  // فقط برای type === "repeater"
  min_rows?: number;
  max_rows?: number | null;
  columns?: FormFieldDef[];
  // فقط برای type === "number" | "select" (AM-006 AM-03)
  chain_rules?: ChainRule[];
}

export interface FormSection {
  fields: FormFieldDef[];
}

export interface FormFooterSection extends FormSection {
  signature_required?: boolean;
}

export interface FormBinding {
  required: boolean;
  allowed_models: string[]; // "app_label.model"
}

export interface FormLayout {
  columns: 1 | 2;
}

export interface FormDefinitionJson {
  title: string;
  layout: FormLayout;
  header: FormSection;
  body: FormSection;
  footer: FormFooterSection;
  binding?: FormBinding;
  inherit_from_parent?: string[];
  schema: Record<string, unknown>;
}

/** AM-006 AM-02: schema برای هر فیلد، شامل نوع repeater */
function fieldToJsonSchemaProp(f: FormFieldDef): Record<string, unknown> {
  switch (f.type) {
    case "number":
      return { type: "number", title: f.label };
    case "checkbox":
      return { type: "boolean", title: f.label };
    case "select":
      return { type: "string", title: f.label, enum: (f.options || []).map((o) => o.value) };
    case "repeater": {
      const itemProps: Record<string, unknown> = {};
      const itemRequired: string[] = [];
      for (const col of f.columns || []) {
        itemProps[col.key] = fieldToJsonSchemaProp(col);
        if (col.required) itemRequired.push(col.key);
      }
      return {
        type: "array",
        title: f.label,
        minItems: f.min_rows ?? 0,
        ...(f.max_rows ? { maxItems: f.max_rows } : {}),
        items: { type: "object", properties: itemProps, required: itemRequired },
      };
    }
    default:
      return { type: "string", title: f.label };
  }
}

function sectionToProps(section: FormSection): { properties: Record<string, unknown>; required: string[] } {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  for (const f of section.fields) {
    properties[f.key] = fieldToJsonSchemaProp(f);
    if (f.required) required.push(f.key);
  }
  return { properties, required };
}

/** ADR-087 FRM-02 (اصلاحشده طبق AM-006): schema از header+body+footer مشتق میشود. */
export function buildJsonSchema(header: FormSection, body: FormSection, footer: FormSection): Record<string, unknown> {
  const h = sectionToProps(header);
  const b = sectionToProps(body);
  const f = sectionToProps(footer);
  return {
    type: "object",
    properties: { ...h.properties, ...b.properties, ...f.properties },
    required: [...h.required, ...b.required, ...f.required],
    additionalProperties: false,
  };
}

function validateFieldsUnique(fields: FormFieldDef[], seen: Set<string>): string | null {
  for (const f of fields) {
    if (!f.key) return "همهٔ فیلدها باید key داشته باشند.";
    if (!/^[a-z][a-z0-9_]*$/.test(f.key)) return `key نامعتبر: ${f.key} (فقط snake_case لاتین)`;
    if (seen.has(f.key)) return `key تکراری: ${f.key}`;
    seen.add(f.key);
    if (f.type === "repeater") {
      for (const col of f.columns || []) {
        if (!col.key) return `ستون داخل ${f.key} باید key داشته باشد.`;
        if (!/^[a-z][a-z0-9_]*$/.test(col.key)) return `key ستون نامعتبر: ${col.key}`;
      }
    }
    if (f.chain_rules?.length && f.type !== "number" && f.type !== "select") {
      return `chain_rules فقط روی number/select مجاز است (فیلد: ${f.key})`;
    }
  }
  return null;
}

export function validateDefinitionKeys(def: Pick<FormDefinitionJson, "header" | "body" | "footer">): string | null {
  const seen = new Set<string>();
  return (
    validateFieldsUnique(def.header.fields, seen) ||
    validateFieldsUnique(def.body.fields, seen) ||
    validateFieldsUnique(def.footer.fields, seen)
  );
}

export function emptyDefinition(title: string): FormDefinitionJson {
  return {
    title,
    layout: { columns: 1 },
    header: { fields: [] },
    body: { fields: [] },
    footer: { fields: [], signature_required: false },
    binding: { required: false, allowed_models: [] },
    inherit_from_parent: [],
    schema: {},
  };
}
